import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { FileText, Loader2, Download, Search, Filter } from 'lucide-react';
import InsuranceAdminNotice from '@/components/InsuranceAdminNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COVERAGE_TYPES = [
  { code: 'ALL', name: 'All coverage types', rate: null },
  { code: 'A', name: 'A · Shipping Insurance Full',          rate: 0.0020 },
  { code: 'B', name: 'B · Shipping Insurance Shared Risk',   rate: 0.0012 },
  { code: 'C', name: 'C · Shipping + Chargeback Coverage',   rate: 0.0100 },
  { code: 'D', name: 'D · Full Benefits & Coverages',        rate: 0.0125 },
];

export default function InsuranceReportingPage() {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  // Field 1: minimum declared value filter (0 = no filter)
  const [minDeclared, setMinDeclared] = useState(0);
  // Field 2: insurance/warranty type filter
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/insurance/broker/ledger?days=90`, { headers: auth });
      if (!r.ok) throw new Error((await r.json()).detail || 'Load failed');
      const d = await r.json();
      setRows(d.records || d.entries || d.rows || []);
    } catch (e) {
      toast.error(e.message);
      setRows([]);
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const list = rows || [];
    return list.filter((r) => {
      if (minDeclared > 0 && Number(r.declared_value || 0) < minDeclared) return false;
      if (typeFilter !== 'ALL' && (r.program_code || '').toUpperCase() !== typeFilter) return false;
      return true;
    });
  }, [rows, minDeclared, typeFilter]);

  const totals = useMemo(() => {
    const list = filtered;
    return {
      count: list.length,
      declared: list.reduce((s, r) => s + Number(r.declared_value || 0), 0),
      premium: list.reduce((s, r) => s + Number(r.pure_premium || 0), 0),
    };
  }, [filtered]);

  const exportCsv = () => {
    const headers = ['authorization_number', 'merchant', 'timestamp', 'tracking_number', 'coverage_type', 'declared_value', 'pure_premium', 'commodity', 'from_zip', 'to_zip', 'carrier_service'];
    const lines = [headers.join(',')].concat(filtered.map((r) => [
      r.authorization_number || '',
      (r.merchant || '').replace(/,/g, ' '),
      r.timestamp || '',
      r.tracking_number || '',
      r.program_code || '',
      Number(r.declared_value || 0).toFixed(2),
      Number(r.pure_premium || 0).toFixed(2),
      (r.commodity || '').replace(/,/g, ' '),
      r.from_zip || '',
      r.to_zip || '',
      (r.carrier_service || '').replace(/,/g, ' '),
    ].join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `insurance-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="insurance-reporting-page">
      <InsuranceAdminNotice />

      <div>
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-500" /> Insurance Reporting
        </h2>
        <p className="text-slate-500 mt-1">Filter and export the Parcel Risk coverage ledger.</p>
      </div>

      {/* 3 fields */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" /> Report filters
          </CardTitle>
          <CardDescription>Set the minimum declared value and coverage type to run the report.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Declared value (min)</Label>
              <Input
                type="number"
                min="0"
                value={minDeclared || ''}
                onChange={(e) => setMinDeclared(Number(e.target.value) || 0)}
                placeholder="0"
                data-testid="filter-declared"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Insurance or warranty type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COVERAGE_TYPES.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">&nbsp;</Label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={load} disabled={busy} data-testid="refresh-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Refresh
                </Button>
                <Button onClick={exportCsv} disabled={!filtered.length} data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary strip */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Card><CardContent className="pt-5">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Coverages</p>
          <p className="text-3xl font-semibold mt-1 tabular-nums" data-testid="stat-count">{totals.count}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total declared</p>
          <p className="text-3xl font-semibold mt-1 tabular-nums" data-testid="stat-declared">${totals.declared.toLocaleString()}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total premium</p>
          <p className="text-3xl font-semibold mt-1 tabular-nums text-emerald-600" data-testid="stat-premium">${totals.premium.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
        </CardContent></Card>
      </div>

      {/* Field 3: Access table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Coverage ledger ({filtered.length})</CardTitle>
          <CardDescription>Every coverage issued in the last 90 days.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead>
                <tr className="text-left border-b border-slate-200 dark:border-slate-800">
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Auth #</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Merchant</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Date &amp; time</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Tracking #</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Type</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">Commodity</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase">From → To</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase text-right">Declared</th>
                  <th className="py-2 px-2 font-semibold text-slate-500 text-xs uppercase text-right">Premium</th>
                </tr>
              </thead>
              <tbody>
                {rows === null ? (
                  <tr><td colSpan={9} className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="py-8 text-center text-slate-400">No coverages match these filters.</td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.authorization_number || r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`row-${(r.authorization_number || '').slice(-6)}`}>
                    <td className="py-2 px-2 font-mono text-xs">{r.authorization_number || '—'}</td>
                    <td className="py-2 px-2">{r.merchant || '—'}</td>
                    <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}</td>
                    <td className="py-2 px-2 font-mono text-xs">{r.tracking_number || '—'}</td>
                    <td className="py-2 px-2">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700">
                        {r.program_code || '—'}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-slate-600 dark:text-slate-300 capitalize">{r.commodity || '—'}</td>
                    <td className="py-2 px-2 whitespace-nowrap text-slate-500 tabular-nums">{r.from_zip || '—'} → {r.to_zip || '—'}</td>
                    <td className="py-2 px-2 text-right tabular-nums">${Number(r.declared_value || 0).toLocaleString()}</td>
                    <td className="py-2 px-2 text-right tabular-nums font-medium text-emerald-600">${Number(r.pure_premium || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
